import { useRef, useEffect, useState, useCallback } from 'react'

interface DiffractionSpiralProps {
  className?: string
  animated?: boolean
}

// Vertex shader - apenas passa coordenadas
const VERTEX_SHADER = `
  attribute vec2 a_position;
  attribute vec2 a_texCoord;
  varying vec2 v_texCoord;
  
  void main() {
    gl_Position = vec4(a_position, 0.0, 1.0);
    v_texCoord = a_texCoord;
  }
`

// Fragment shader - difração baseado no Alan Zucconi
const FRAGMENT_SHADER = `
  precision highp float;
  
  varying vec2 v_texCoord;
  
  uniform vec2 u_resolution;
  uniform vec2 u_textureSize;
  uniform vec2 u_offset;
  uniform sampler2D u_normalMap;
  uniform sampler2D u_alphaMap;
  uniform float u_time;
  
  // Spectral Zucconi 6 - difração de luz
  float saturateVal(float x) {
    return min(1.0, max(0.0, x));
  }
  
  vec3 saturateVec(vec3 x) {
    return min(vec3(1.0), max(vec3(0.0), x));
  }
  
  vec3 bump3y(vec3 x, vec3 yoffset) {
    vec3 y = vec3(1.0) - x * x;
    y = saturateVec(y - yoffset);
    return y;
  }
  
  vec3 spectral_zucconi6(float x) {
    const vec3 c1 = vec3(3.54585104, 2.93225262, 2.41593945);
    const vec3 x1 = vec3(0.69549072, 0.49228336, 0.27699880);
    const vec3 y1 = vec3(0.02312639, 0.15225084, 0.52607955);
    
    const vec3 c2 = vec3(3.90307140, 3.21182957, 3.96587128);
    const vec3 x2 = vec3(0.11748627, 0.86755042, 0.66077860);
    const vec3 y2 = vec3(0.84897130, 0.88445281, 0.73949448);
    
    return bump3y(c1 * (x - x1), y1) + bump3y(c2 * (x - x2), y2);
  }
  
  void main() {
    // Corrigir aspect ratio (cover fit)
    vec2 uv = v_texCoord;
    float screenAspect = u_resolution.x / u_resolution.y;
    float textureAspect = 1.0; // Assumindo textura quadrada
    
    // Ajustar UV para manter proporção (cover)
    if (screenAspect > textureAspect) {
        float scale = textureAspect / screenAspect;
        uv.y = (uv.y - 0.5) * scale * (screenAspect/textureAspect) + 0.5; // Ajuste empírico
        // Na verdade, para cover, queremos:
        // uv.y = (uv.y - 0.5) * (screenAspect / textureAspect) + 0.5;
        // Mas vamos simplificar: usar coordenadas de tela normalizadas
    } else {
        // float scale = screenAspect / textureAspect;
        // uv.x = (uv.x - 0.5) * scale + 0.5;
    }
    
    // Implementação Cover correta
    vec2 ratio = vec2(
      max((u_resolution.x/u_resolution.y) / (u_textureSize.x/u_textureSize.y), 1.0),
      max((u_resolution.y/u_resolution.x) / (u_textureSize.y/u_textureSize.x), 1.0)
    );
    
    // Zoom in na textura (diminuir UV range = aumentar textura)
    vec2 uv_cover = (v_texCoord - 0.5) * ratio * 0.65 + 0.5; // Multiplicador 0.65 dá zoom

    // Se estiver fora da textura, descartar ou repetir (clamp)
    // Vamos usar clamp to edge nas configurações da textura, aqui apenas sampleamos
    
    // Sample normal map com bilinear filtering
    vec4 normalSample = texture2D(u_normalMap, uv_cover);
    // Converter de [0,1] para [-1,1]
    vec3 normal = normalize(normalSample.xyz * 2.0 - 1.0);
    // Inverter X se necessário (depende de como a textura foi gerada)
    normal.x = -normal.x; 
    
    // Sample alpha map
    vec4 alphaSample = texture2D(u_alphaMap, uv_cover);
    float alpha = alphaSample.a;
    
    // Calcular difração
    // Usar u_time para animar a luz mesmo parado
    vec2 animatedOffset = u_offset + vec2(sin(u_time * 0.5) * 0.15, cos(u_time * 0.4) * 0.15);
    
    // Vetor do pixel até a fonte de luz (offset)
    vec2 toLight = v_texCoord - animatedOffset;
    float distToLight = length(toLight);
    vec3 lightDir = normalize(vec3(toLight, 0.4)); // Z controla a "altura" da luz
    
    // Cálculo de difração Zucconi
    // O segredo é misturar a distância radial com a normal da superfície
    float incidence = dot(normal, lightDir); // -1 a 1
    float diffractionIndex = distToLight - (incidence * 0.5); // Varia a cor baseada na inclinação
    
    // Escalar para ter mais ciclos de cor (arco-íris repetido)
    vec3 color = spectral_zucconi6(diffractionIndex * 1.5); 
    
    // Specular highlight (brilho branco onde a luz bate direto)
    float specular = pow(max(0.0, incidence), 4.0) * 1.5;
    color += vec3(specular);
    
    // Ambient light para não ficar preto total
    color += vec3(0.05, 0.05, 0.1); 

    // Aumentar vibração
    color = pow(color, vec3(0.6)); // Gamma correction
    color *= 1.2; // Exposure

    // Aplicar alpha da máscara com fade nas bordas
    float edgeFade = smoothstep(0.0, 0.1, uv_cover.x) * smoothstep(1.0, 0.9, uv_cover.x) *
                     smoothstep(0.0, 0.1, uv_cover.y) * smoothstep(1.0, 0.9, uv_cover.y);
    
    gl_FragColor = vec4(color, alpha * edgeFade);
  }
`

function createShader(gl: WebGLRenderingContext, type: number, source: string): WebGLShader | null {
  const shader = gl.createShader(type)
  if (!shader) return null

  gl.shaderSource(shader, source)
  gl.compileShader(shader)

  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.error('Shader compile error:', gl.getShaderInfoLog(shader))
    gl.deleteShader(shader)
    return null
  }

  return shader
}

function createProgram(
  gl: WebGLRenderingContext,
  vertexShader: WebGLShader,
  fragmentShader: WebGLShader
): WebGLProgram | null {
  const program = gl.createProgram()
  if (!program) return null

  gl.attachShader(program, vertexShader)
  gl.attachShader(program, fragmentShader)
  gl.linkProgram(program)

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    console.error('Program link error:', gl.getProgramInfoLog(program))
    gl.deleteProgram(program)
    return null
  }

  return program
}

function loadTexture(gl: WebGLRenderingContext, url: string): Promise<WebGLTexture | null> {
  return new Promise((resolve) => {
    const texture = gl.createTexture()
    const image = new Image()

    image.onload = () => {
      gl.bindTexture(gl.TEXTURE_2D, texture)
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
      resolve(texture)
    }

    image.onerror = () => resolve(null)
    image.src = url
  })
}

/**
 * Diffraction Spiral - Efeito holográfico estilo Apple Cash
 * - Usa WebGL com shader de difração
 * - Responde a movimento do mouse/gyro
 * - Textura normal map gerada no Blender
 */
export function DiffractionSpiral({ className = '' }: DiffractionSpiralProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const glRef = useRef<WebGLRenderingContext | null>(null)
  const programRef = useRef<WebGLProgram | null>(null)
  const animationRef = useRef<number>(0)
  const offsetRef = useRef({ x: 0.5, y: 0.5 })
  const targetOffsetRef = useRef({ x: 0.5, y: 0.5 })
  const isHoveringRef = useRef(false)
  const [ready, setReady] = useState(false)

  // Smooth offset interpolation & Idle Animation
  const updateOffset = useCallback((time: number) => {
    // Se não estiver interagindo, animar em círculo suave
    if (!isHoveringRef.current) {
      const slowTime = time * 0.5
      targetOffsetRef.current = {
        x: 0.5 + Math.cos(slowTime) * 0.3,
        y: 0.5 + Math.sin(slowTime * 0.7) * 0.2,
      }
    }

    const lerp = 0.05 // Mais suave
    offsetRef.current.x += (targetOffsetRef.current.x - offsetRef.current.x) * lerp
    offsetRef.current.y += (targetOffsetRef.current.y - offsetRef.current.y) * lerp
  }, [])

  // Mouse/touch handler
  const handlePointerMove = useCallback((e: PointerEvent | MouseEvent) => {
    isHoveringRef.current = true
    const canvas = canvasRef.current
    if (!canvas) return

    const rect = canvas.getBoundingClientRect()
    const x = (e.clientX - rect.left) / rect.width
    const y = (e.clientY - rect.top) / rect.height

    // Limitar range do efeito
    targetOffsetRef.current.x = 0.3 + x * 0.4
    targetOffsetRef.current.y = 0.3 + y * 0.4
  }, [])

  // Gyroscope handler (mobile)
  const handleDeviceOrientation = useCallback((e: DeviceOrientationEvent) => {
    if (e.gamma === null || e.beta === null) return

    // gamma: -90 to 90 (left to right tilt)
    // beta: -180 to 180 (front to back tilt)
    const x = 0.5 + (e.gamma / 90) * 0.3
    const y = 0.5 + ((e.beta - 45) / 90) * 0.3

    targetOffsetRef.current.x = Math.max(0.2, Math.min(0.8, x))
    targetOffsetRef.current.y = Math.max(0.2, Math.min(0.8, y))
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const gl = canvas.getContext('webgl', { alpha: true, premultipliedAlpha: false })
    if (!gl) {
      console.error('WebGL not supported')
      return
    }
    glRef.current = gl

    // Setup canvas size
    const dpr = window.devicePixelRatio || 1
    const rect = canvas.getBoundingClientRect()
    canvas.width = rect.width * dpr
    canvas.height = rect.height * dpr
    gl.viewport(0, 0, canvas.width, canvas.height)

    // Create shaders
    const vertexShader = createShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER)
    const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER)
    if (!vertexShader || !fragmentShader) return

    const program = createProgram(gl, vertexShader, fragmentShader)
    if (!program) return
    programRef.current = program

    // Setup geometry (full screen quad)
    const positions = new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1])
    const texCoords = new Float32Array([0, 1, 1, 1, 0, 0, 0, 0, 1, 1, 1, 0])

    const positionBuffer = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer)
    gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW)

    const positionLoc = gl.getAttribLocation(program, 'a_position')
    gl.enableVertexAttribArray(positionLoc)
    gl.vertexAttribPointer(positionLoc, 2, gl.FLOAT, false, 0, 0)

    const texCoordBuffer = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, texCoordBuffer)
    gl.bufferData(gl.ARRAY_BUFFER, texCoords, gl.STATIC_DRAW)

    const texCoordLoc = gl.getAttribLocation(program, 'a_texCoord')
    gl.enableVertexAttribArray(texCoordLoc)
    gl.vertexAttribPointer(texCoordLoc, 2, gl.FLOAT, false, 0, 0)

    // Load textures
    console.log('Loading textures...')
    Promise.all([
      loadTexture(gl, '/textures/normal.png'),
      loadTexture(gl, '/textures/alpha.png'),
    ]).then(([normalMap, alphaMap]) => {
      if (!normalMap || !alphaMap) {
        console.error('Failed to load textures - checks /public/textures/')
        return
      }
      console.log('Textures loaded successfully')

      gl.useProgram(program)

      // Set texture uniforms
      const normalMapLoc = gl.getUniformLocation(program, 'u_normalMap')
      const alphaMapLoc = gl.getUniformLocation(program, 'u_alphaMap')

      gl.activeTexture(gl.TEXTURE0)
      gl.bindTexture(gl.TEXTURE_2D, normalMap)
      gl.uniform1i(normalMapLoc, 0)

      gl.activeTexture(gl.TEXTURE1)
      gl.bindTexture(gl.TEXTURE_2D, alphaMap)
      gl.uniform1i(alphaMapLoc, 1)

      // Set resolution
      const resolutionLoc = gl.getUniformLocation(program, 'u_resolution')
      gl.uniform2f(resolutionLoc, canvas.width, canvas.height)

      const textureSizeLoc = gl.getUniformLocation(program, 'u_textureSize')
      gl.uniform2f(textureSizeLoc, 512, 512) // Assuming texture size

      setReady(true)
    })

    // Event listeners
    const handleEnter = () => {
      isHoveringRef.current = true
    }
    const handleLeave = () => {
      isHoveringRef.current = false
    }

    canvas.addEventListener('pointermove', handlePointerMove)
    canvas.addEventListener('mousemove', handlePointerMove)
    canvas.addEventListener('pointerenter', handleEnter)
    canvas.addEventListener('pointerleave', handleLeave)

    if (typeof DeviceOrientationEvent !== 'undefined') {
      window.addEventListener('deviceorientation', handleDeviceOrientation)
    }

    return () => {
      canvas.removeEventListener('pointermove', handlePointerMove)
      canvas.removeEventListener('mousemove', handlePointerMove)
      canvas.removeEventListener('pointerenter', handleEnter)
      canvas.removeEventListener('pointerleave', handleLeave)
      window.removeEventListener('deviceorientation', handleDeviceOrientation)
      cancelAnimationFrame(animationRef.current)
    }
  }, [handlePointerMove, handleDeviceOrientation])

  // Render loop
  useEffect(() => {
    if (!ready) return

    const gl = glRef.current
    const program = programRef.current
    if (!gl || !program) return

    const offsetLoc = gl.getUniformLocation(program, 'u_offset')
    const timeLoc = gl.getUniformLocation(program, 'u_time')

    let time = 0

    const render = () => {
      time += 0.01
      updateOffset(time)

      gl.useProgram(program)
      gl.uniform2f(offsetLoc, offsetRef.current.x, offsetRef.current.y)
      gl.uniform1f(timeLoc, time)

      gl.enable(gl.BLEND)
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)

      gl.clearColor(0, 0, 0, 0)
      gl.clear(gl.COLOR_BUFFER_BIT)
      gl.drawArrays(gl.TRIANGLES, 0, 6)

      time += 0.016
      animationRef.current = requestAnimationFrame(render)
    }

    render()

    return () => cancelAnimationFrame(animationRef.current)
  }, [ready, updateOffset])

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 w-full h-full ${className}`}
      style={{ opacity: ready ? 1 : 0, transition: 'opacity 0.5s ease-out' }}
    />
  )
}
